import { ThemeEvents } from '@theme/events';

/**
 * Reveals the sticky results toolbar's bottom border + shadow only while it is
 * "stuck" under the header, matching the Figma "on scroll" state.
 *
 * A zero-height sentinel is rendered right before each `.facets-toggle`. While
 * the sentinel is scrolled up past the header offset it stops intersecting the
 * viewport and we flag the toolbar as stuck via a `data-stuck` attribute.
 *
 * Purely presentational: the IntersectionObserver does the work off the main
 * thread — no layout is read or written on scroll. Because the collection
 * section is re-rendered (morphed) on every filter/sort change, we re-bind the
 * observers after each `filter:update`, so the effect keeps working without a
 * full page reload.
 */
(() => {
  /** @type {IntersectionObserver | null} */
  let observer = null;

  const headerHeight = () =>
    parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 64;

  const bind = () => {
    // Rebuild the observer so it never holds a stale sentinel after a morph.
    observer?.disconnect();
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const toggle = entry.target.nextElementSibling;
          if (toggle?.classList.contains('facets-toggle')) {
            toggle.toggleAttribute('data-stuck', entry.intersectionRatio === 0);
          }
        }
      },
      { threshold: [0, 1], rootMargin: `-${headerHeight()}px 0px 0px 0px` }
    );

    document.querySelectorAll('.facets-toggle__sentinel').forEach((sentinel) => {
      observer?.observe(sentinel);
    });
  };

  const init = () => {
    bind();
    // The section is morphed on filter/sort changes; the sentinel may be a new
    // node, so re-bind on the next frame once the DOM has settled.
    document.addEventListener(ThemeEvents.FilterUpdate, () => {
      requestAnimationFrame(bind);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
